/**
 * Cross-tab refresh coordinator — Web Locks + BroadcastChannel glue (D-01/D-03).
 *
 * Responsibilities:
 *   - Elect a single leader across tabs via `navigator.locks.request(AUTH_LOCK_NAME, ...)`
 *     (Chrome 69+ / FF 96+ / Safari 15.4+ per the browser baseline in CONTEXT).
 *   - Leader fires `performRefresh()`; followers see an already-fresh token in
 *     the double-check and return it without a second /auth/refresh call.
 *   - Leader broadcasts `TOKEN_REFRESHED` on `BroadcastChannel("auth")` so
 *     followers can prime their own in-memory store without waiting for the
 *     lock to turn over.
 *   - `setupAuthChannelHandlers` is the exhaustive BC subscriber that React
 *     AuthProvider will register (Plan 05) — `TOKEN_REFRESHED` / `LOGOUT` /
 *     `FAMILY_REVOKED` cover all current variants.
 *   - `configureAuthNetwork({baseUrl})` is a module-local one-time init for
 *     the /api/v1/auth/* endpoints. Rationale: `@linkhr/auth` is non-React and
 *     baseUrl differs per app — arg-injection over `import.meta.env` keeps the
 *     package testable. App bootstrap (Plan 05) calls this once from
 *     createAppRoot before any auth action runs.
 *
 * NOTE: the `performRefresh` network call lives in `./performRefresh.ts` but
 * consumes `_baseUrl` via the `getAuthBaseUrl()` getter exported here so both
 * modules share one source of truth.
 */
import { match } from "ts-pattern";
import {
  AUTH_LOCK_NAME,
  REFRESH_SKEW_SECONDS,
  assertUnreachable,
  type AuthBroadcastMessage,
} from "../channel/broadcastSchema.js";
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { decodeJwtExp } from "../tokens/jwt.js";
import {
  REFRESH_NETWORK_ERROR,
  REFRESH_SERVER_ERROR,
  performRefresh,
  setRefreshCooldownMemory,
} from "./performRefresh.js";
import {
  scheduleProactiveRefresh,
  clearProactiveRefresh,
  refreshSettled,
  retryRefreshLater,
} from "./proactiveTimer.js";

// Network config + BroadcastChannel singleton live in authNetworkConfig.ts
// (extracted to break a no-circular cycle with performRefresh.ts).
// Re-export so existing consumers of crossTabCoordinator's surface still work,
// AND import locally so the leader/follower logic below can call them.
import {
  configureAuthNetwork,
  getAuthBaseUrl,
  getRefreshPath,
  getLogoutPath,
  getAudience,
  getAudienceHeader,
  getAuthChannel,
  subscribeToAuthChannel,
} from "./authNetworkConfig.js";
export {
  configureAuthNetwork,
  getAuthBaseUrl,
  getRefreshPath,
  getLogoutPath,
  getAudience,
  getAudienceHeader,
  getAuthChannel,
  subscribeToAuthChannel,
};

// ─── cross-tab single-flight refresh ────────────────────────────────────────

/**
 * Elect a single leader (across all tabs on this origin) to run /auth/refresh.
 *
 * Flow inside the lock:
 *  1. Read the current access token from `accessTokenStore`.
 *  2. If a fresh token (exp - now > REFRESH_SKEW_SECONDS) is already present
 *     — e.g. a prior leader's BC TOKEN_REFRESHED primed this tab's store —
 *     return it without a new fetch. This is the critical no-double-fetch
 *     path that makes concurrent tabs cheap.
 *  3. Otherwise perform the refresh, write the new token, and post
 *     TOKEN_REFRESHED so other tabs' BC subscriptions can pick it up.
 *
 * Callers (the api-client's refresh middleware via `TokenProvider.tryRefresh`,
 * or the proactive timer) NEVER need to await the BC round-trip; they receive
 * the fresh token as the promise resolution.
 */
export interface TryRefreshCrossTabResult {
  accessToken: string;
  /**
   * Permission codes fetched alongside the token rotation (Plan 08.1-02).
   * `undefined` when the leader hit a fresh-token short-circuit (no rotation
   * happened, so no fresh perms either) OR when the perms fetch failed.
   * Followers + callers that need post-refresh perms hydration can either
   * trust their last-known Set or fall back to the BC subscriber.
   */
  effectivePermissions?: readonly string[];
  /**
   * The refresh response's `mustChangePassword`. `undefined` on the
   * fresh-token short-circuit (no rotation happened, so no answer).
   */
  mustChangePassword?: boolean;
}

/**
 * Cross-tab single-flight refresh — back-compat wrapper around the richer
 * {@link tryRefreshCrossTabFull} that returns just the access token. Used by
 * the proactive timer (fire-and-forget) and the api-client TokenProvider's
 * 401 retry seam, both of which only care about the token. Plan 08.1-02
 * preserves this signature so those call sites don't churn.
 */
export async function tryRefreshCrossTab(): Promise<string> {
  try {
    const { accessToken } = await tryRefreshCrossTabFull();
    refreshSettled();
    return accessToken;
  } catch (err) {
    // No answer, or a 5xx: nothing was said about the session, so it stays
    // and the refresh is tried again later. Anything else (a 401, a revoked
    // family) is a verdict: never retried here, and the server is plainly
    // reachable, so "can't reach the server" goes.
    const code = (err as { errorCode?: unknown } | null)?.errorCode;
    if (code === REFRESH_NETWORK_ERROR || code === REFRESH_SERVER_ERROR) retryRefreshLater();
    else refreshSettled();
    throw err;
  }
}

/**
 * Cross-tab single-flight refresh — full result including perms. Used by
 * `refreshOnLoad` (cold-boot bootstrap) so the snapshot can hydrate
 * `effectivePermissions` in one round-trip without a separate fetch.
 * The leader fires `performRefresh` (which itself calls
 * `fetchEffectivePermissions` after token rotation); followers see the
 * fresh-token short-circuit and return without perms — they can pick up
 * perms from the BC `TOKEN_REFRESHED` message the leader posts.
 */
/**
 * The refresh currently in flight IN THIS TAB, shared by every concurrent
 * caller.
 *
 * Web Locks serializes refreshes across the whole origin, so where it exists
 * this is merely redundant. Where it does NOT exist — insecure plain-http dev
 * hosts — it is the only thing standing between two callers and a double
 * rotation, and the two callers genuinely race: the proactive timer fires
 * `tryRefreshCrossTab` on its own schedule while the api-client's 401
 * middleware calls it through `TokenProvider.tryRefresh`. That middleware has
 * its own mutex, but it only covers concurrent 401s inside one closure; it
 * knows nothing about the timer.
 *
 * A double rotation is not a wasted request. The second one presents a refresh
 * token the server has already retired, which is indistinguishable from theft —
 * so the family is revoked and the user is signed out mid-session.
 */
let inflightRefresh: Promise<TryRefreshCrossTabResult> | null = null;
/** The audience the in-flight refresh was started for — its token belongs to
 * that audience and to no other. */
let inflightAud: ReturnType<typeof getAudience> | null = null;

/**
 * A refresh that finished after the audience moved on. Its token is for the
 * audience it started in, so it is neither stored, broadcast nor handed back:
 * doing any of those files an admin session under `mobile` (or the reverse).
 */
const AUDIENCE_CHANGED = "REFRESH_AUDIENCE_CHANGED";

export async function tryRefreshCrossTabFull(): Promise<TryRefreshCrossTabResult> {
  // Join the refresh already running rather than starting a second one. The
  // outcome is shared, failure included: a caller that arrived during a failing
  // refresh should see that failure, not silently start another attempt.
  // Only when it is running for THIS audience, though — the global login
  // re-points the audience mid-probe, and the other audience's token is not
  // this caller's session.
  const existing = inflightRefresh;
  if (existing !== null && inflightAud === getAudience()) return await existing;

  const startedAud = getAudience();
  const run = startRefresh(startedAud);
  inflightRefresh = run;
  inflightAud = startedAud;
  try {
    return await run;
  } finally {
    // Only clear if this is still the live attempt — a later call may already
    // have installed its own.
    if (inflightRefresh === run) {
      inflightRefresh = null;
      inflightAud = null;
    }
  }
}

async function startRefresh(
  startedAud: ReturnType<typeof getAudience>,
): Promise<TryRefreshCrossTabResult> {
  // Web Locks exists only in secure contexts. Plain-http dev hosts other than
  // localhost (e.g. the *.linkhr.test /etc/hosts subdomains) are insecure, so
  // `navigator.locks` is undefined there — and an unguarded `.request` throws
  // BEFORE any network, which the boot path converts into an instant silent
  // logout on every hard navigation. Degrade to a tab-local single-flight: the
  // CROSS-tab coordination is lost (two tabs may still both rotate, which the
  // server handles as sequential rotations), but within a tab the promise
  // mutex above keeps it to one.
  if (typeof navigator === "undefined" || navigator.locks === undefined) {
    return await refreshUnderLock(startedAud);
  }
  // `request` resolves to whatever the callback returns — a promise here, so
  // the await flattens it rather than handing back a nested one.
  return await navigator.locks.request(AUTH_LOCK_NAME, { mode: "exclusive" }, () =>
    refreshUnderLock(startedAud),
  );
}

/** The critical section run by the lock leader (or directly when Web Locks is unavailable). */
async function refreshUnderLock(
  startedAud: ReturnType<typeof getAudience>,
): Promise<TryRefreshCrossTabResult> {
  // Same reason as the post-request check below: the store holds ONE token and
  // the audience may have moved while this call queued for the lock.
  if (getAudience() !== startedAud) {
    throw Object.assign(new Error(`Refresh discarded: ${AUDIENCE_CHANGED}`), {
      errorCode: AUDIENCE_CHANGED,
      status: 0,
    });
  }
  const current = accessTokenStore.get();
  if (current !== null) {
    const exp = decodeJwtExp(current);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp !== null && exp - nowSec > REFRESH_SKEW_SECONDS) {
      // Fresh token already in store — no rotation, no perms re-fetch.
      // Followers in this branch trust the BC handler to have already
      // primed perms during the leader's most recent refresh.
      return { accessToken: current };
    }
  }
  const { accessToken, exp, issuedAt, effectivePermissions, mustChangePassword } =
    await performRefresh();
  // The audience moved while the request was out (the global login probes
  // `admin` then `mobile`). This token is the OLD audience's — storing it,
  // broadcasting it on the new audience's channel or returning it to a caller
  // that now means the other app all hand a session to the wrong surface.
  if (getAudience() !== startedAud) {
    throw Object.assign(new Error(`Refresh discarded: ${AUDIENCE_CHANGED}`), {
      errorCode: AUDIENCE_CHANGED,
      status: 0,
    });
  }
  accessTokenStore.set(accessToken, exp, issuedAt);
  getAuthChannel().postMessage({
    type: "TOKEN_REFRESHED",
    accessToken,
    exp,
    issuedAt,
    ...(effectivePermissions !== undefined ? { effectivePermissions } : {}),
    mustChangePassword,
  } satisfies AuthBroadcastMessage);
  // BroadcastChannel does NOT echo a posted message back to the sender, so
  // the leader tab never runs the TOKEN_REFRESHED subscriber on ITS OWN
  // rotation. Reschedule the leader's proactive timer here (mirroring the
  // subscriber) — otherwise the leader's timer dies after one refresh and it
  // silently stops proactively refreshing until a reactive 401 or reload.
  // (The leader's own React snapshot perms still refresh via the return value
  // on bootstrap/401 paths; a leader's fire-and-forget proactive rotation
  // leaves the snapshot's perms until the next cross-tab message or reload —
  // tolerable since the access token in the store is always current.)
  scheduleProactiveRefresh();
  return effectivePermissions !== undefined
    ? { accessToken, effectivePermissions, mustChangePassword }
    : { accessToken, mustChangePassword };
}

// ─── BC message subscriber (exhaustive ts-pattern) ──────────────────────────

export interface SetupAuthChannelHandlersOpts {
  onTokenRefreshed: (msg: Extract<AuthBroadcastMessage, { type: "TOKEN_REFRESHED" }>) => void;
  onLogout: () => void;
  onFamilyRevoked: (msg: Extract<AuthBroadcastMessage, { type: "FAMILY_REVOKED" }>) => void;
}

/**
 * Wire the three BC message variants to caller-supplied callbacks.
 *
 * Exhaustive via `ts-pattern`'s `.exhaustive()` — adding a 4th variant to
 * `AuthBroadcastMessage` surfaces as a compile error here. For each variant:
 *   - TOKEN_REFRESHED: store the new token + reschedule proactive timer BEFORE
 *     invoking caller callback (so React state update sees the store ready).
 *   - LOGOUT: clear store + clear timer, then caller callback (router redirect).
 *   - FAMILY_REVOKED: same as LOGOUT but caller callback carries the
 *     correlationId for logging + UX ("signed out for security reasons").
 *
 * Returns the unsubscribe handle for React cleanup.
 */
export function setupAuthChannelHandlers(opts: SetupAuthChannelHandlersOpts): () => void {
  const handle = (msg: AuthBroadcastMessage): void =>
    match(msg)
      .with({ type: "TOKEN_REFRESHED" }, (m) => {
        accessTokenStore.set(m.accessToken, m.exp, m.issuedAt);
        scheduleProactiveRefresh();
        // Another tab reached the server: this one can stop saying it can't,
        // and refetch what failed meanwhile.
        refreshSettled();
        opts.onTokenRefreshed(m);
      })
      .with({ type: "LOGOUT" }, () => {
        accessTokenStore.clear();
        clearProactiveRefresh();
        opts.onLogout();
      })
      .with({ type: "FAMILY_REVOKED" }, (m) => {
        accessTokenStore.clear();
        clearProactiveRefresh();
        opts.onFamilyRevoked(m);
      })
      .with({ type: "REFRESH_COOLDOWN" }, (m) => {
        // Mirror another tab's refresh-throttle cooldown into this tab's
        // memory — the second transport for storage-disabled environments
        // where localStorage can't share it. Handled internally; no caller
        // callback (React state doesn't care about throttle bookkeeping).
        setRefreshCooldownMemory(m.untilMs);
      })
      .exhaustive();
  ownHandlers.add(handle);
  const unsubscribe = subscribeToAuthChannel(handle);
  return () => {
    ownHandlers.delete(handle);
    unsubscribe();
  };
}

/** This tab's channel handlers (see `deliverToThisTab`). */
const ownHandlers = new Set<(msg: AuthBroadcastMessage) => void>();

/**
 * Run this tab's channel handlers for a message it would also post: a
 * BroadcastChannel delivers to every other tab and never to the sender.
 */
export function deliverToThisTab(msg: AuthBroadcastMessage): void {
  ownHandlers.forEach((handle) => {
    handle(msg);
  });
}

// Re-exported so consumers (performRefresh, tests) don't need to reach into
// internal helpers to silence unused-export lint warnings.
export { assertUnreachable };
